'use client';

import React, { useState, KeyboardEvent } from 'react';
import { ChatState } from './ChatLayout';

interface GeoInterestBarProps {
  geoEnabled: boolean;
  onGeoToggle: (v: boolean) => void;
  interests: string[];
  onInterestsChange: (tags: string[]) => void;
  chatState: ChatState;
  gender: string;
  onGenderChange: (g: string) => void;
}

export default function GeoInterestBar({
  geoEnabled,
  onGeoToggle,
  interests,
  onInterestsChange,
  chatState,
  gender,
  onGenderChange,
}: GeoInterestBarProps) {
  const [tagInput, setTagInput] = useState('');
  const [geoStatus, setGeoStatus] = useState<'idle' | 'granted' | 'denied'>('idle');

  const disabled = chatState === 'chatting' || chatState === 'searching' || chatState === 'connecting';

  const handleGeoToggle = () => {
    if (disabled) return;
    if (!geoEnabled) {
      navigator.geolocation?.getCurrentPosition(
        () => { setGeoStatus('granted'); onGeoToggle(true); },
        () => { setGeoStatus('denied'); },
        { enableHighAccuracy: true }
      );
    } else {
      onGeoToggle(false);
      setGeoStatus('idle');
    }
  };

  const handleTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
      e.preventDefault();
      const tag = tagInput.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
      if (tag && !interests.includes(tag) && interests.length < 5) {
        onInterestsChange([...interests, tag]);
      }
      setTagInput('');
    }
    if (e.key === 'Backspace' && !tagInput && interests.length > 0) {
      onInterestsChange(interests.slice(0, -1));
    }
  };

  const removeTag = (tag: string) => {
    if (!disabled) onInterestsChange(interests.filter((t) => t !== tag));
  };

  const genderOptions = [
    { value: 'any', label: 'Any' },
    { value: 'male', label: '♂ Male' },
    { value: 'female', label: '♀ Female' },
  ];

  return (
    <div className="bg-card border-b border-border px-4 xl:px-6 py-2.5 flex items-center gap-4 flex-wrap flex-shrink-0">
      
      {/* Gender Filter */}
      <div className="flex items-center gap-1.5">
        <svg className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
        <div className="flex gap-1">
          {genderOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => !disabled && onGenderChange(opt.value)}
              disabled={disabled}
              className={`text-xs px-2.5 py-1 rounded-full font-medium transition-all duration-150 ${
                gender === opt.value
                  ? 'bg-primary text-white'
                  : 'bg-border/60 text-muted-foreground hover:bg-border'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="w-px h-4 bg-border hidden sm:block" />

      {/* Geo Toggle */}
      <div className="flex items-center gap-2.5">
        <button
          onClick={handleGeoToggle}
          disabled={disabled}
          className={`relative rounded-full transition-all duration-200 ${
            geoEnabled ? 'bg-primary' : 'bg-border'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'}`}
          style={{ width: '40px', height: '22px' }}
          aria-label="Toggle geo matching"
        >
          <span
            className={`absolute top-0.5 rounded-full bg-white shadow transition-all duration-200 ${
              geoEnabled ? 'left-[18px]' : 'left-0.5'
            }`}
            style={{ width: '18px', height: '18px' }}
          />
        </button>
        <div className="flex items-center gap-1.5">
          <svg className={`w-3.5 h-3.5 ${geoEnabled ? 'text-primary' : 'text-muted-foreground'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="text-xs font-medium text-foreground">5km Local</span>
          {geoStatus === 'denied' && <span className="text-xs text-destructive">(Location denied)</span>}
          {geoStatus === 'granted' && geoEnabled && <span className="text-xs text-accent">(Active)</span>}
        </div>
      </div>

      <div className="w-px h-4 bg-border hidden sm:block" />

      {/* Interest Tags */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <svg className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
        </svg>
        <div className="flex items-center gap-1.5 flex-wrap">
          {interests.map((tag) => (
            <span key={`tag-${tag}`} className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs font-medium px-2 py-0.5 rounded-full">
              {tag}
              {!disabled && (
                <button onClick={() => removeTag(tag)} className="hover:text-primary/60 transition-colors">
                  <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </span>
          ))}
          {!disabled && interests.length < 5 && (
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagKeyDown}
              placeholder={interests.length === 0 ? 'Add interests (press Enter)...' : 'Add more...'}
              className="text-xs bg-transparent outline-none text-foreground placeholder:text-muted-foreground min-w-[120px]"
              maxLength={20}
            />
          )}
          {disabled && interests.length === 0 && (
            <span className="text-xs text-muted-foreground">No interests set</span>
          )}
        </div>
      </div>

      <span className="text-xs text-muted-foreground hidden lg:block">{interests.length}/5 tags</span>
    </div>
  );
}
